import {
  UndefinedInitialDataOptions,
  useQueries,
  useQuery,
} from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import { Entity, mapEntity, MappedEntity } from '@toggle/toggle';

import {
  postEntities,
  postEntitiesRequest,
} from '~/api/entities/entity-service';
import { useAPI } from '~/hooks/use-api/useAPI';

export interface UseEntityReturn {
  loading: boolean;
  error?: Error;
  data?: MappedEntity[];
}

interface UseEntityParams {
  entities: string[];
  byId?: boolean;
}

type UseEntities = (params: UseEntityParams) => UseEntityReturn;

export enum FILTER {
  id = 1,
  tag = 2,
}

const makeRequestBody = (entities: string[], byId: boolean) => ({
  filter: byId ? FILTER.id : FILTER.tag,
  args: entities,
});

const mapEntities = (entities: Entity[]) => entities.map(mapEntity);

/** USE useEntitiesWithReactQuery
 * @deprecated
 */
export const useEntities: UseEntities = ({ entities, byId = false }) => {
  const { data, loading, error } = useAPI<Entity[], undefined, MappedEntity[]>(
    signal => postEntities(makeRequestBody(entities, byId), signal),
    {
      initialState: { data: undefined, loading: true },
      shouldFetch: !!entities.length,
      deps: [[...entities].sort((a, b) => a.localeCompare(b)).toString(), byId],
      mapDataFn: next => mapEntities(next),
    }
  );

  return {
    loading,
    error,
    data,
  };
};

export const QUERY_KEY_ENTITIES = 'QUERY_KEY_ENTITIES';

export const useEntitiesWithReactQuery = (entities: string[], byId = false) => {
  const queries = entities.map(entityTag => ({
    queryKey: [QUERY_KEY_ENTITIES, { entityTag, byId }],
    queryFn: () => postEntitiesRequest(makeRequestBody([entityTag], byId)),
  }));
  return useQueries({
    queries,
    combine: results => {
      return {
        data: results
          .flatMap(result => result.data)
          .filter(Boolean) as Entity[],
        isLoading: results.some(
          result => result.isLoading || result.isRefetching
        ),
        isError: results.every(result => result.error),
        refetch: () => {
          results.forEach(result => result.refetch());
        },
      };
    },
  });
};

export const QUERY_KEY_ENTITY = 'QUERY_KEY_ENTITY';
export const createEntityQueryOptions = (
  entityTag: string
): UndefinedInitialDataOptions<Entity[], Error, Entity[], string[]> => ({
  queryKey: [QUERY_KEY_ENTITY, entityTag],
  queryFn: ({ signal }) =>
    postEntitiesRequest(makeRequestBody([entityTag], false), signal),
  staleTime: getTimeTillEndOfDayMs(),
  enabled: !!entityTag,
});

export const useEntity = (entityTag: string) =>
  useQuery(createEntityQueryOptions(entityTag));
